import { Box, SxProps, Theme } from '@mui/material'
import React, { useEffect } from 'react'
import { Widget, WidgetConfig, WidgetType } from '../classes/widgets/Widget'
import { WidgetView } from './WidgetView'
import { PageTitle } from './atoms/PageTitle'
import { t } from 'i18next'
import { PortType } from '../types/ports'
import { useMessageFilter } from '../hooks/useMessageFilter'
import { SocketMessage } from '../types/messaging'

export interface WidgetGridProps {
  widgets: Widget[]
  onRefresh: () => void
  configure?: boolean
  sx?: SxProps<Theme>
}

// Convert to Finnish if the appliance's name contains default name of some port types
const formatName = (name: string, portType: PortType) => {
  if (name.includes(t(`PortType-${portType}`, { lng: 'en' }))) {
    const translatedNameInEn = t(`PortType-${portType}`, { lng: 'en' })
    if (name.trim().length === translatedNameInEn.length) {
      return t(`PortType-${portType}`)
    } else {
      return t(`PortType-${portType}`) + name.slice(translatedNameInEn.length - name.length)
    }
  }
  return name
}

export function WidgetGrid(props: WidgetGridProps): React.ReactElement {
  const { sx, widgets, onRefresh, configure } = props
  const seen = new Set<WidgetType>()
  const handleGenerateName = (w: Widget<{}, WidgetConfig>) => {
    let result: string | undefined = ''
    // Some widgets don't have appliances array such as “PRICE_STAT”
    // and this leads to no any “name” property inside >>> front-page crashed
    if (w.appliances.length !== 0) {
      const applianceType = w.appliances[0]?.type
      if (w.type === 'POWER_GRAPH' && applianceType === 'ENERGY_METER') {
        const portType = w.appliances[0]?.controls[0]?.port?.type
        if (w.appliances[0]?.name && portType) {
          result = formatName(w.appliances[0]?.name, portType)
        }
      }
    }
    return result
  }
  const handleGenerateTitle = (w: Widget<{}, WidgetConfig>) => {
    let result: string | undefined = ''
    switch (w.type) {
      case 'POWER_GRAPH':
        if (w.appliances[0]?.type === 'SOLAR_PANEL') {
          result = w.dashboardTitle(!seen.has(w.type))
        }
        break
      case 'PRICE_GRAPH':
        result = w.dashboardTitle(!seen.has(w.type))
        break
    }
    return result
  }

  useEffect(() => {
    widgets.filter(w => w.visible).forEach(w => w.enter())
    return () => {
      widgets.filter(w => w.visible).forEach(w => w.exit())
    }
  }, [widgets])

  useMessageFilter({
    filter: () => true,
    isReady: () => false,
    onMatch: async (msg: SocketMessage) => {
      for (const w of widgets.filter(w => w.visible)) {
        if (w.needRefreshOnMessage(msg)) {
          await w.fetchData()
          await w.beforeRefresh()
          onRefresh()
        }
      }
    },
    depends: [widgets]
  })

  return (
    <Box sx={{
      ...sx,
      display: 'grid',
      gridTemplateColumns: '1fr 1fr 1fr 1fr',
      gridAutoFlow: 'row',
      gridColumnGap: '1.2rem',
      gridRowGap: '0.7rem',
    }}>
      {widgets.filter(w => configure || w.visible).map(w => {
        const title = handleGenerateTitle(w)
        const name = handleGenerateName(w)
        seen.add(w.type)
        return <React.Fragment key={w.id}>
          {!!name && <PageTitle sx={{ gridColumnEnd: 'span 4', mt: 6 }} title={name}/>}
          {!!title && <PageTitle sx={{ gridColumnEnd: 'span 4', mt: 6 }} title={title}/>}
          <WidgetView configure={configure} widget={w} onRefresh={onRefresh}/>
        </React.Fragment>
      })}
    </Box>
  )
}
